import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card, SectionTitle } from "@/components/shared/primitives";
import { relativeLastActive, TRACKING_LABELS } from "@/lib/format";

export default function Extension() {
  const { data } = useQuery({ queryKey: ["ext-health"], queryFn: () => api.get("/settings/extension") });
  const { data: health } = useQuery({ queryKey: ["health"], queryFn: () => api.get("/health") });

  const people = data?.data?.people || [];
  const detection = data?.data?.detection_health || [];

  const isStale = (iso) => {
    if (!iso) return true;
    return (new Date() - new Date(iso)) / 86400000 > 3;
  };
  const personStatus = (p) => {
    if (!p.installed) return { label: "Not installed", color: "var(--nb-ink-2)" };
    if (isStale(p.last_event)) return { label: "May be broken", color: "var(--nb-amber)" };
    return { label: "Healthy", color: "var(--nb-moss)" };
  };

  const installed = people.filter((p) => p.installed).length;
  const healthy = people.filter((p) => p.installed && !isStale(p.last_event)).length;

  return (
    <div className="space-y-6">
      <Card className="p-5" data-testid="extension-health">
        <SectionTitle className="mb-1">Detection health by person</SectionTitle>
        <p className="text-[13px] text-nb-ink2 mb-4">
          The extension runs in each person's managed browser profile, so health is tracked per person. {healthy} of {installed} installs are reporting ·
          last event received: {health?.data?.last_event_at ? relativeLastActive(health.data.last_event_at) : "-"}
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-[14px]">
            <thead><tr className="text-[13px] text-nb-ink2 text-left border-b border-nb-border">
              <th className="py-2 font-medium">Person</th><th className="py-2 font-medium">Team</th><th className="py-2 font-medium">Office</th>
              <th className="py-2 font-medium">Last event</th><th className="py-2 font-medium">Status</th>
            </tr></thead>
            <tbody>
              {people.map((p) => {
                const st = personStatus(p);
                return (
                  <tr key={p.member_id} className="border-b border-nb-border last:border-0" data-testid={`ext-person-${p.member_id}`}>
                    <td className="py-3 text-nb-ink">{p.name}</td>
                    <td className="py-3 text-nb-ink2">{p.team || "-"}</td>
                    <td className="py-3 text-nb-ink2">{p.location || "-"}</td>
                    <td className="py-3 text-nb-ink2">{p.installed ? (p.last_event ? relativeLastActive(p.last_event) : "No events yet") : "-"}</td>
                    <td className="py-3">
                      <span className="inline-flex items-center gap-1.5 text-[13px] text-nb-ink">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: st.color }} />{st.label}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {people.length === 0 && <tr><td colSpan={5} className="py-4 text-[14px] text-nb-ink2">No people yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-5" data-testid="extension-rules">
        <SectionTitle className="mb-1">Detection rules by tool</SectionTitle>
        <p className="text-[13px] text-nb-ink2 mb-4">How many feature rules each tool has, and when one last matched an event.</p>
        <div className="overflow-x-auto">
          <table className="w-full text-[14px]">
            <thead><tr className="text-[13px] text-nb-ink2 text-left border-b border-nb-border"><th className="py-2 font-medium">Tool</th><th className="py-2 font-medium">Rules</th><th className="py-2 font-medium">Last matched</th><th className="py-2 font-medium">Tracking</th></tr></thead>
            <tbody>
              {detection.map((h) => (
                <tr key={h.app_id} className="border-b border-nb-border last:border-0">
                  <td className="py-3 text-nb-ink">{h.app}</td>
                  <td className="py-3 tnum">{h.rules}</td>
                  <td className="py-3 text-nb-ink2">{h.last_matched_at ? relativeLastActive(h.last_matched_at) : "-"}</td>
                  <td className="py-3 text-nb-ink2">{TRACKING_LABELS[h.tracking_source]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
